import { isValidPhoneNumber } from '@/utils/phoneUtils';

export const STANDBY_CLIENT_SOURCE = {
  QR: 'qr',
  LINK: 'link',
  EMAIL: 'email',
  SMS: 'sms',
  PUSH: 'push',
  DIRECT: 'direct',
} as const;

export type StandbyClientSource =
  (typeof STANDBY_CLIENT_SOURCE)[keyof typeof STANDBY_CLIENT_SOURCE];

/**
 * Enable gate for the public standby phone entry: the app's strict phone
 * validator, the same rule the booking and details forms apply.
 *
 * SUPERSEDES (2026-08-21) the NANP typed-digit-count rule this flow shipped
 * with. Counting digits accepted fictional 555 numbers on purpose, so a typo in
 * a real number could pass the gate and burn an SMS; real validation is applied
 * to every country alike instead. Test runs use strictly valid numbers.
 */
export const isAcceptableStandbyPhone = (value: string): boolean =>
  isValidPhoneNumber(value);

export const resolveStandbyClientSource = (
  src?: string,
): StandbyClientSource => {
  const values = Object.values(STANDBY_CLIENT_SOURCE);
  return values.includes(src as StandbyClientSource)
    ? (src as StandbyClientSource)
    : STANDBY_CLIENT_SOURCE.DIRECT;
};

// SQR-14765: the offer_viewed analytics source is a 4-value enum; the legacy
// list-join sources (qr/link) aren't offer entry points, so they — and anything
// unrecognized — collapse to `direct`.
export const STANDBY_OFFER_SOURCE = {
  EMAIL: 'email',
  SMS: 'sms',
  PUSH: 'push',
  DIRECT: 'direct',
} as const;

export type StandbyOfferSource =
  (typeof STANDBY_OFFER_SOURCE)[keyof typeof STANDBY_OFFER_SOURCE];

export const toStandbyOfferSource = (
  source: StandbyClientSource,
): StandbyOfferSource =>
  source === STANDBY_CLIENT_SOURCE.EMAIL ||
  source === STANDBY_CLIENT_SOURCE.SMS ||
  source === STANDBY_CLIENT_SOURCE.PUSH
    ? source
    : STANDBY_OFFER_SOURCE.DIRECT;
